import { DailyVideo, useParticipantProperty } from '@daily-co/daily-react';
import { Box } from '@mui/material';
import { useCallback, useState } from 'react';

import { CenteredName, ParticipantBadge } from './VideoBadges';

interface ParticipantTileProps {
  sessionId: string;
  variant: 'main' | 'thumbnail' | 'grid' | 'gridFill';
  isLocal?: boolean;
  isActiveSpeaker?: boolean;
  /** Render DailyVideo with type="screenVideo" */
  isScreenShare?: boolean;
}

const variantSx = {
  main: { width: '100%', height: '100%' },
  thumbnail: { width: '100%', aspectRatio: '16/9' },
  grid: { width: '100%', aspectRatio: '16/9' },
  gridFill: { width: '100%', height: '100%' },
} as const;

export function ParticipantTile({
  sessionId,
  variant,
  isLocal = false,
  isActiveSpeaker = false,
  isScreenShare = false,
}: ParticipantTileProps) {
  const userName = useParticipantProperty(sessionId, 'user_name');
  const video = useParticipantProperty(sessionId, 'video');

  const [videoAR, setVideoAR] = useState<number | null>(null);

  const handleResize = useCallback(({ aspectRatio }: { aspectRatio: number }) => {
    setVideoAR(aspectRatio);
  }, []);

  const objectFit = isScreenShare
    ? 'contain'
    : videoAR !== null && videoAR >= 1
      ? 'cover'
      : 'contain';

  const displayName = isLocal ? (userName ? `${userName} (You)` : 'You') : userName || 'Guest';
  const showVideo = isScreenShare || video;

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'grey.800',
        borderRadius: 2,
        overflow: 'hidden',
        ...(isActiveSpeaker
          ? { outlineOffset: -2, outline: '2px solid', outlineColor: 'primary.main' }
          : {}),
        ...variantSx[variant],
      }}
    >
      {showVideo ? (
        <DailyVideo
          sessionId={sessionId}
          type={isScreenShare ? 'screenVideo' : 'video'}
          mirror={isLocal && !isScreenShare}
          onResize={handleResize}
          style={{
            width: '100%',
            height: '100%',
            objectFit,
          }}
        />
      ) : (
        <CenteredName name={displayName} />
      )}

      <ParticipantBadge sessionId={sessionId} isLocal={isLocal} />
    </Box>
  );
}
